/**
 * Parse dashboard `sections` from the API.
 *
 * The backend may return a normal string array, a single TOON-style inline list
 * (e.g. `{IT, Infra, "Cap Goods"}`), or that list incorrectly split on commas
 * into fragments like `["{IT", "Infra", ..., "\"My Portfolio\"}"]`.
 */

const stripToonListWrapper = (text) => {
  let s = String(text || "").trim();
  if (!s) return "";

  // Optional TOON array header, e.g. `sections[30]:`, `sections[30]{name}:`, or `[30]:`
  const headerMatch = /^(?:[\w.-]+)?\[\d+\](?:\{[^}]*\})?\s*:\s*/.exec(s);
  if (headerMatch) {
    s = s.slice(headerMatch[0].length).trim();
  }

  if (s.startsWith("{") && s.endsWith("}")) {
    s = s.slice(1, -1).trim();
  }

  return s;
};

const unquoteSectionToken = (token) => {
  let s = String(token || "").trim();
  if (!s) return "";

  if (s.startsWith('\\"') && s.endsWith('\\"')) {
    s = s.slice(2, -2);
  } else if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1);
  }

  return s.replace(/\\"/g, '"').trim();
};

/** Split on commas while respecting double-quoted segments. */
const splitDelimitedValues = (text, delimiter = ",") => {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
        current += char;
      }
    } else if (char === delimiter && !inQuotes) {
      const piece = current.trim();
      if (piece) result.push(piece);
      current = "";
    } else {
      current += char;
    }
  }

  const tail = current.trim();
  if (tail) result.push(tail);

  return result;
};

const looksLikeToonFragments = (items) => {
  if (!Array.isArray(items) || items.length === 0) return false;

  const first = String(items[0]).trim();
  const last = String(items[items.length - 1]).trim();

  if (first.startsWith("{") || last.endsWith("}")) return true;

  const hasEscapedQuoteFragments = items.some((item) => {
    const t = String(item).trim();
    return /^\\"/.test(t) || /\\"$/.test(t);
  });

  if (hasEscapedQuoteFragments) return true;

  // Some responses split quoted values without backslashes:
  // ["\"Parent", "Child\""] should be treated as one token after join.
  const hasUnbalancedQuoteFragments = items.some((item) => {
    const t = String(item).trim();
    const startsQuoted = t.startsWith('"');
    const endsQuoted = t.endsWith('"');
    return startsQuoted !== endsQuoted;
  });

  return hasUnbalancedQuoteFragments;
};

/**
 * @param {unknown} raw - API `sections` field (array, string, or null)
 * @returns {string[]}
 */
export const parseDashboardSections = (raw) => {
  if (raw == null) return [];

  if (typeof raw === "string") {
    const inner = stripToonListWrapper(raw);
    if (!inner) return [];
    return splitDelimitedValues(inner).map(unquoteSectionToken).filter(Boolean);
  }

  if (!Array.isArray(raw)) return [];

  const trimmed = raw.map((item) => String(item).trim()).filter(Boolean);
  if (trimmed.length === 0) return [];

  if (!looksLikeToonFragments(trimmed)) {
    return trimmed.map(unquoteSectionToken).filter(Boolean);
  }

  const joined = trimmed.join(", ");
  const inner = stripToonListWrapper(joined);
  if (!inner) return [];

  return splitDelimitedValues(inner).map(unquoteSectionToken).filter(Boolean);
};

export default parseDashboardSections;
